import React, { useLayoutEffect, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import dataLayer from '@rover/globals/dataLayer';
import ChatInboxRight from '@rover/icons/svg/chat-inbox-right.svg';
import { Box, Flex, Text } from '@rover/kibble/core';
import { LINE_HEIGHT } from '@rover/kibble/official-patterns/OverflowText/OverflowText.common';
import useTheme from '@rover/kibble/themes/useTheme';

import { Props } from './ExpandableReviewSnippet.common';
import {
  COLLAPSED_REVIEW_LINE_COUNT,
  EXPANDED_REVIEW_LINE_COUNT,
  REVIEW_LINK_FADE,
  SEARCH_RESULT_CARD_Z_INDEX,
} from './SearchResultCard.constants';

const ExpandableReviewSnippet = ({ reviewText, reviewPetType }: Props): React.ReactElement => {
  const { t } = useLingui();
  const theme = useTheme();
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollHeight > textRef.current.clientHeight);
    }
  }, [reviewText]);

  const lineCount = isExpanded ? EXPANDED_REVIEW_LINE_COUNT : COLLAPSED_REVIEW_LINE_COUNT;
  const maxHeight = lineCount * LINE_HEIGHT;
  const linkColor = theme.colors.interactive_link.primary_base.toString();

  const handleReadMoreClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(true);
    dataLayer?.push({
      event: 'sitter-card-review-read-more-button-click',
    });
  };

  return (
    <Flex gap="2x" data-testid={`review-snippet-${reviewPetType}-highlight`}>
      <Box sx={{ minWidth: '20px' }}>
        <ChatInboxRight height="20px" width="20px" fill={theme.textColor.primary} />
      </Box>
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          maxHeight: `${maxHeight}px`,
          overflow: 'hidden',
        }}
      >
        <Box
          ref={textRef}
          sx={{
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitLineClamp: lineCount,
            WebkitBoxOrient: 'vertical',
          }}
        >
          <Text size="200" textColor="secondary">
            &ldquo;{reviewText}&rdquo;
          </Text>
        </Box>
        {isTruncated && !isExpanded && (
          <div
            data-testid="overflow-text-button"
            role="button"
            tabIndex={0}
            onClick={handleReadMoreClick}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleReadMoreClick(e as unknown as React.MouseEvent);
              }
            }}
            style={{
              background: REVIEW_LINK_FADE,
              position: 'absolute',
              bottom: 0,
              right: 0,
              paddingLeft: '75px',
              cursor: 'pointer',
              zIndex: SEARCH_RESULT_CARD_Z_INDEX.INTERACTIVE,
            }}
          >
            <Text size="200" sx={{ color: linkColor }}>
              {t`Read more`}
            </Text>
          </div>
        )}
      </Box>
    </Flex>
  );
};

export default ExpandableReviewSnippet;
